import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Button,
  Link,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Container,
  Tabs,
} from '@cloudscape-design/components';
import MermaidDiagram from '../../components/MermaidDiagram';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, #0f2027 0%, #203a43 40%, #2c5364 100%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

function ObservabilityPageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('observability');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;

  const codeStyle = {
    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
    padding: '1rem',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    border: `1px solid ${isDark ? '#334155' : '#e9ebed'}`,
  };

  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid gridDefinition={[{ colspan: { default: 12, s: 8 } }]}>
            <Box padding="s">
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                Observability for
                Multi-Agent Systems
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                Trace, debug, and monitor agent performance from development through production
                using OpenTelemetry, Strands Agents SDK, and Amazon Bedrock AgentCore Observability.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">

        {/* Section 1: Strands Agents Observability */}
        <div id="strands-observability">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Strands Agents SDK Observability
            </Header>
          </Box>

          <TextContent>
            <p>
              The Strands Agents SDK embeds observability directly into the framework. All telemetry APIs — traces, 
              metrics, and logs — are built-in, requiring no external instrumentation libraries to get started. 
              The SDK natively integrates with <strong>OpenTelemetry</strong>, the industry standard for distributed tracing.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <TextContent>
                  <h3>📊 Traces</h3>
                  <p>
                    End-to-end request traces with hierarchical spans capturing model invocations, 
                    tool executions, and reasoning cycles. Each span is enriched with context — prompts, 
                    token usage, parameters, and results.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>📈 Metrics</h3>
                  <p>
                    Automatic tracking of token usage (input/output/cache), execution latency, 
                    tool call counts and error rates, reasoning cycle durations, and model API performance.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>📝 Logs</h3>
                  <p>
                    Structured logging at each decision point. Capture agent reasoning, tool selection 
                    rationale, and error context for debugging complex multi-step workflows.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>

          <Box margin={{ top: "xl" }}>
            <Header variant="h2">Trace Structure</Header>
            <TextContent>
              <p>
                Strands creates a hierarchical trace structure that mirrors agent execution. Each trace 
                captures the complete journey of a request through the agent system:
              </p>
            </TextContent>
            <Box margin={{ vertical: "m" }}>
              <MermaidDiagram
                id="strands-trace-structure"
                chart={`
                  graph TD
                    A["Agent Span<br/>(entire invocation)"] --> B["Cycle Span 1<br/>(reasoning cycle)"]
                    A --> C["Cycle Span 2<br/>(reasoning cycle)"]
                    B --> D["LLM Span<br/>(model invocation)"]
                    B --> E["Tool Span<br/>(tool execution)"]
                    C --> F["LLM Span<br/>(model invocation)"]
                    C --> G["Tool Span<br/>(tool execution)"]
                    
                    style A fill:#232f3e,stroke:#fff,stroke-width:2px,color:#fff
                    style B fill:#ff9900,stroke:#fff,stroke-width:2px,color:#fff
                    style C fill:#ff9900,stroke:#fff,stroke-width:2px,color:#fff
                    style D fill:#146eb4,stroke:#fff,stroke-width:2px,color:#fff
                    style E fill:#1b660f,stroke:#fff,stroke-width:2px,color:#fff
                    style F fill:#146eb4,stroke:#fff,stroke-width:2px,color:#fff
                    style G fill:#1b660f,stroke:#fff,stroke-width:2px,color:#fff
                `}
              />
            </Box>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection 
              headerText="Captured Trace Attributes" 
              variant="container"
              headerDescription="Rich metadata captured at each span level"
            >
              <Tabs
                tabs={[
                  {
                    label: "Agent-Level",
                    id: "agent-attrs",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <TextContent>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e9ebed' }}>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Attribute</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.system</code></td><td style={{ padding: '6px 12px' }}>Agent system identifier ("strands-agents")</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.agent.name</code></td><td style={{ padding: '6px 12px' }}>Name of the agent</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.request.model</code></td><td style={{ padding: '6px 12px' }}>Model ID used by the agent</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.usage.total_tokens</code></td><td style={{ padding: '6px 12px' }}>Total token usage across the invocation</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.usage.cache_read_input_tokens</code></td><td style={{ padding: '6px 12px' }}>Tokens read from prompt cache</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.user.message</code></td><td style={{ padding: '6px 12px' }}>The user's initial prompt</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.choice</code></td><td style={{ padding: '6px 12px' }}>The agent's final response</td></tr>
                            </tbody>
                          </table>
                        </TextContent>
                      </Box>
                    )
                  },
                  {
                    label: "Tool-Level",
                    id: "tool-attrs",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <TextContent>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e9ebed' }}>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Attribute</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.tool.name</code></td><td style={{ padding: '6px 12px' }}>Name of the tool called</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.tool.call.id</code></td><td style={{ padding: '6px 12px' }}>Unique identifier for the tool call</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>tool.status</code></td><td style={{ padding: '6px 12px' }}>Execution status (success/error)</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.event.start_time</code></td><td style={{ padding: '6px 12px' }}>When tool execution began</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.event.end_time</code></td><td style={{ padding: '6px 12px' }}>When tool execution completed</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.choice</code></td><td style={{ padding: '6px 12px' }}>Formatted tool result</td></tr>
                            </tbody>
                          </table>
                        </TextContent>
                      </Box>
                    )
                  },
                  {
                    label: "Model Invoke",
                    id: "model-attrs",
                    content: (
                      <Box margin={{ vertical: "m" }}>
                        <TextContent>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e9ebed' }}>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Attribute</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.request.model</code></td><td style={{ padding: '6px 12px' }}>Model ID (e.g., "amazon.nova-pro-v1:0")</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.usage.prompt_tokens</code></td><td style={{ padding: '6px 12px' }}>Tokens used for prompts</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.usage.completion_tokens</code></td><td style={{ padding: '6px 12px' }}>Tokens used for completions</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.user.message</code></td><td style={{ padding: '6px 12px' }}>Formatted prompt sent to model</td></tr>
                              <tr><td style={{ padding: '6px 12px' }}><code>gen_ai.choice</code></td><td style={{ padding: '6px 12px' }}>Response from the model</td></tr>
                            </tbody>
                          </table>
                        </TextContent>
                      </Box>
                    )
                  }
                ]}
              />
            </ExpandableSection>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="SDK Metrics Access (Code Example)"
              variant="container"
              headerDescription="Accessing performance metrics from AgentResult"
            >
              <pre style={codeStyle}>
{`from strands import Agent
from strands_tools import calculator

agent = Agent(tools=[calculator])
result = agent("What is the square root of 144?")

# Access metrics through AgentResult
print(f"Total tokens: {result.metrics.accumulated_usage['totalTokens']}")
print(f"Execution time: {sum(result.metrics.cycle_durations):.2f} seconds")
print(f"Tools used: {list(result.metrics.tool_metrics.keys())}")

# Cache metrics (when available)
if 'cacheReadInputTokens' in result.metrics.accumulated_usage:
    print(f"Cache read tokens: {result.metrics.accumulated_usage['cacheReadInputTokens']}")`}
              </pre>
            </ExpandableSection>
          </Box>
        </div>

        {/* Section 2: AgentCore Observability */}
        <div id="agentcore-observability">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Amazon Bedrock AgentCore Observability
            </Header>
          </Box>

          <TextContent>
            <p>
              AgentCore Observability provides production-grade monitoring for agent systems deployed at scale. 
              It delivers real-time visibility into operational performance through purpose-built CloudWatch dashboards, 
              with standardised OpenTelemetry-compatible telemetry that integrates with existing monitoring stacks.
            </p>
          </TextContent>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>🔍 Detailed Workflow Visualisations</h3>
                  <p>
                    See each step in the agent workflow with rich trace visualisations. 
                    Click into any trace to explore the full execution trajectory and timeline.
                  </p>

                  <h3>📊 GenAI-Specific Dashboard</h3>
                  <p>
                    Purpose-built CloudWatch dashboard with three views: Agents (all agents with runtime metrics), 
                    Sessions (multi-turn conversation tracking), and Traces (span-level execution detail).
                  </p>

                  <h3>🔗 Session Correlation</h3>
                  <p>
                    Correlate traces across multiple agent runs using OpenTelemetry baggage. 
                    Track multi-turn conversations end-to-end with session IDs.
                  </p>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>⚡ Real-Time Telemetry</h3>
                  <p>
                    Key metrics tracked automatically: session count, latency, duration, 
                    token usage, and error rates — all available in real-time.
                  </p>

                  <h3>🏗️ Framework Agnostic</h3>
                  <p>
                    Works with all AI agent frameworks (Strands, LangChain, CrewAI) and any LLM. 
                    Standardised OTEL format integrates with Datadog, Dynatrace, Grafana, and more.
                  </p>

                  <h3>🚀 Zero-Code Instrumentation</h3>
                  <p>
                    Agents deployed on AgentCore Runtime get automatic instrumentation with no code changes. 
                    Self-managed agents configure via environment variables.
                  </p>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>

          <Box margin={{ top: "xl" }}>
            <Header variant="h2">Two Deployment Models</Header>
            <Box margin={{ top: "m" }}>
              <ColumnLayout columns={2} variant="text-grid">
                <Container header={<Header variant="h3">AgentCore Runtime-Hosted</Header>}>
                  <TextContent>
                    <p>
                      Agents deployed within AgentCore Runtime get <strong>automatic instrumentation</strong> with 
                      minimal configuration. The fastest path to production observability.
                    </p>
                    <ul>
                      <li>Zero-code trace collection</li>
                      <li>Automatic CloudWatch integration</li>
                      <li>Built-in GenAI dashboard</li>
                      <li>Session tracking out of the box</li>
                    </ul>
                  </TextContent>
                </Container>
                <Container header={<Header variant="h3">Self-Managed Infrastructure</Header>}>
                  <TextContent>
                    <p>
                      For agents running on ECS, EC2, Lambda, or other infrastructure — configure OTEL 
                      environment variables to send telemetry to CloudWatch.
                    </p>
                    <ul>
                      <li>Same observability features as runtime-hosted</li>
                      <li>Configure via environment variables</li>
                      <li>Uses AWS Distro for OpenTelemetry (ADOT)</li>
                      <li>Consistent monitoring regardless of deployment target</li>
                    </ul>
                  </TextContent>
                </Container>
              </ColumnLayout>
            </Box>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="Configuration for Self-Managed Agents"
              variant="container"
              headerDescription="Environment variables and setup for non-runtime agents"
            >
              <pre style={codeStyle}>
{`# Install Strands with OTEL support
pip install 'strands-agents[otel]'
pip install aws-opentelemetry-distro

# Configure environment variables
export AGENT_OBSERVABILITY_ENABLED=true
export OTEL_PYTHON_DISTRO=aws_distro
export OTEL_PYTHON_CONFIGURATOR=aws_configurator
export OTEL_EXPORTER_OTLP_PROTOCOL=http/protobuf
export OTEL_EXPORTER_OTLP_LOGS_HEADERS=x-aws-log-group=<LOG-GROUP>,x-aws-log-stream=<LOG-STREAM>,x-aws-metric-namespace=<NAMESPACE>
export OTEL_RESOURCE_ATTRIBUTES=service.name=<AGENT-NAME>

# Run with automatic instrumentation
opentelemetry-instrument python agent.py`}
              </pre>
            </ExpandableSection>
          </Box>

          <Box textAlign="center" margin={{ top: "l" }}>
            <SpaceBetween direction="horizontal" size="m">
              <Button
                variant="primary"
                href="https://docs.aws.amazon.com/bedrock-agentcore/latest/devguide/observability.html"
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                AgentCore Observability Docs
              </Button>
              <Button
                href="https://strandsagents.com/latest/documentation/docs/user-guide/observability-evaluation/observability/"
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                Strands Observability Guide
              </Button>
            </SpaceBetween>
          </Box>
        </div>

        {/* Section 3: ResearchIQ Implementation */}
        <div id="researchiq-implementation">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '3.5rem 0 3.5rem 0' }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              ResearchIQ Implementation
            </Header>
          </Box>

          <TextContent>
            <p>
              ResearchIQ implements observability across its multi-agent research workflow, providing 
              end-to-end trace visibility from user query through to final report generation. 
              Each agent in the graph is instrumented to capture execution metrics, tool usage, and reasoning steps.
            </p>
          </TextContent>

          <Box margin={{ vertical: "m" }}>
            <MermaidDiagram
              id="researchiq-trace-flow"
              chart={`
                graph LR
                  U["User Query"] --> DR["Document<br/>Researcher"]
                  DR --> EC["Evidence<br/>Compiler"]
                  EC --> CT["Citation<br/>Tracker"]
                  CT --> R["Final<br/>Report"]
                  
                  DR -.- T1["trace: kb_retrieval<br/>tokens: 1,200"]
                  EC -.- T2["trace: cross_doc_analysis<br/>tokens: 2,400"]
                  CT -.- T3["trace: citation_format<br/>tokens: 800"]
                  
                  style U fill:#232f3e,stroke:#fff,stroke-width:2px,color:#fff
                  style DR fill:#ff9900,stroke:#fff,stroke-width:2px,color:#fff
                  style EC fill:#146eb4,stroke:#fff,stroke-width:2px,color:#fff
                  style CT fill:#1b660f,stroke:#fff,stroke-width:2px,color:#fff
                  style R fill:#232f3e,stroke:#fff,stroke-width:2px,color:#fff
              `}
            />
          </Box>

          <Box margin={{ top: "l" }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <TextContent>
                  <h3>What We Trace</h3>
                  <ul>
                    <li><strong>Agent graph execution</strong> — full DAG traversal with per-node timing</li>
                    <li><strong>Knowledge Base retrieval</strong> — query terms, result count, relevance scores</li>
                    <li><strong>Model invocations</strong> — prompt size, completion size, latency per call</li>
                    <li><strong>Tool calls</strong> — retrieve tool parameters, success/failure, duration</li>
                    <li><strong>Token consumption</strong> — per-agent and aggregate across the workflow</li>
                  </ul>
                </TextContent>
              </div>
              <div>
                <TextContent>
                  <h3>What We Monitor</h3>
                  <ul>
                    <li><strong>End-to-end latency</strong> — total time from query to report</li>
                    <li><strong>Per-agent duration</strong> — identify bottlenecks in the pipeline</li>
                    <li><strong>Error rates</strong> — tool failures, model timeouts, retrieval misses</li>
                    <li><strong>Token efficiency</strong> — cost tracking per research question</li>
                    <li><strong>Cache hit rates</strong> — prompt caching effectiveness</li>
                  </ul>
                </TextContent>
              </div>
            </ColumnLayout>
          </Box>

          <Box margin={{ top: "l" }}>
            <ExpandableSection
              headerText="Observability in the Research Graph"
              variant="container"
              headerDescription="How traces propagate through the multi-agent workflow"
            >
              <pre style={codeStyle}>
{`# Each agent in the graph automatically generates spans
# The Strands SDK handles trace propagation between agents

from strands import Agent
from strands.models import BedrockModel

# Configure model with observability
model = BedrockModel(
    model_id="amazon.nova-pro-v1:0",
    region_name="ap-southeast-2",
)

# Create agents — traces are automatic
researcher = Agent(
    name="document_researcher",  # Shows in gen_ai.agent.name attribute
    model=model,
    system_prompt="...",
    tools=[retrieve_tool]
)

# After invocation, access metrics
result = researcher("What was Amazon's revenue in 2024?")
print(f"Tokens used: {result.metrics.accumulated_usage['totalTokens']}")
print(f"Tool calls: {result.metrics.tool_metrics}")
print(f"Cycles: {len(result.metrics.cycle_durations)}")

# Traces are exported automatically via OTEL when configured
# View in CloudWatch → Transaction Search → filter by service.name`}
              </pre>
            </ExpandableSection>
          </Box>
        </div>

      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for "On this page" navigation
function ObservabilityPageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          {
            text: "Strands Agents Observability",
            href: "#strands-observability",
            level: 1
          },
          {
            text: "AgentCore Observability",
            href: "#agentcore-observability",
            level: 1
          },
          {
            text: "ResearchIQ Implementation",
            href: "#researchiq-implementation",
            level: 1
          }
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

function ObservabilityPage() {
  return (
    <AppLayout
      content={<ObservabilityPageContent />}
      tools={<ObservabilityPageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default ObservabilityPage;
